import { SentimentType } from '@aws-sdk/client-comprehend'
import { capitalize, debounce } from 'lodash'
import { Studio } from 'Core/Studio'
// @ts-ignore
import h2p from 'html2plaintext'
import { getSentiment } from 'HTTPClients/plugins.client'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { BlockStateFormatKey, ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
import { Signal } from '@preact/signals-react'
import { ViolationSource } from 'shared/violations/types'

export type SentimentPluginConfigType = {
    /**
     *
     */
}

export const SentimentColorMap: Record<SentimentType, `#${string}`> = {
    MIXED: '#B68C26',
    NEGATIVE: '#8B0000',
    NEUTRAL: '#4169E1',
    POSITIVE: '#006400',
}

export class SentimentPlugin extends BasePlugin<PluginConfigType<SentimentPluginConfigType>> {
    public readonly id = 'Sentiment Analysis'

    constructor(
        config: PluginConfigType<SentimentPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
    }

    public async initialize(studio: Studio) {
        this.studio = studio

        this.pluginConnectors.forEach(connector => {
            const data = connector.getData()
            const connectedBlocks = connector.getConnectedBlocks()
            if (data['text']) {
                data['text'].subscribe(() => {
                    this.onValueChange(connector.id, data, connectedBlocks)
                })
            }
        })
    }

    private getSentimentDetails(sentiment: SentimentType | undefined) {
        if (sentiment === undefined) {
            return
        }
        return { color: SentimentColorMap[sentiment], label: capitalize(sentiment) }
    }

    private onValueChange = debounce(
        async (
            connectorId: string,
            data: Record<string, Signal<unknown> | null>,
            connectedBlocks: ITaskBlock[]
        ) => {
            if (!data['text']) return
            const value = data['text'].value
            const text = h2p(
                data['text'].type === BlockStateFormatKey.Markdown
                    ? convertMarkdownToHTML(value as string)
                    : value
            )
            const sentiment = await this.detectSentiment(text)
            const sentimentDetails = this.getSentimentDetails(sentiment)
            this.handleAnalysisItem(sentimentDetails, connectedBlocks)
            this.handleViolation(connectorId, sentiment)
        },
        1000
    )

    private handleAnalysisItem(
        sentimentItem: { color: `#${string}`; label: string } | undefined,
        connectedBlocks: ITaskBlock[]
    ) {
        if (sentimentItem) {
            connectedBlocks.forEach(block => {
                block.setTags?.('sentiment', {
                    label: 'Sentiment',
                    value: sentimentItem.label,
                    colorHex: sentimentItem.color,
                })
            })
        } else {
            connectedBlocks.forEach(block => {
                block.setTags?.('sentiment', undefined)
            })
        }
    }

    private handleViolation(connectorId: string, sentiment: SentimentType | undefined) {
        if (sentiment && sentiment === 'NEGATIVE') {
            this.studio?.inputManager.addViolation?.({
                type: 'warning',
                id: `${connectorId}_sentiment`,
                message: `Sentiment is ${capitalize(sentiment)}`,
                source: ViolationSource.Plugin,
            })
        } else {
            this.studio?.inputManager.removeViolation?.(`${connectorId}_sentiment`, 'warning')
        }
    }

    private async detectSentiment(text: string): Promise<SentimentType | undefined> {
        if (!text) {
            return
        }

        const data = await getSentiment(text)
        if (!data.isOk()) {
            return
        }
        return data.value.sentiment as SentimentType
    }
}
